import { useMemo, useState } from 'react';
import { Download, KeyRound, Mail, Shield, UserPlus, UserX } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { usePeople, useResendWelcome } from '../../hooks/usePeople';
import { useRoleTemplates } from '../../hooks/useRoles';
import type { Person } from '../../lib/peopleApi';
import { anonymizeUser, downloadGdprExport } from '../../lib/usersApi';
import { CreateTeamMemberModal } from '../people/CreateTeamMemberModal';
import { UserPermissionsEditor } from './UserPermissionsEditor';

/**
 * Settings > Team Management (CLE-216, parent section 3.5). Lists team members,
 * surfaces whether each has a login account, lets admins add a new member (the
 * shared Create Team Member modal), edit a member's per-user permission matrix,
 * resend a welcome email (regenerating the password), download a member's GDPR
 * data export and anonymize ("delete") an account (SaaS hardening).
 */
export function TeamManagementSettings() {
  const { data: people, isLoading } = usePeople({});
  const { data: templates } = useRoleTemplates();
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState<Person | null>(null);
  const [anonymizeTarget, setAnonymizeTarget] = useState<Person | null>(null);

  const sorted = useMemo(
    () => [...(people ?? [])].sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [people],
  );

  if (editingUser && editingUser.userId) {
    return (
      <UserPermissionsEditor
        person={editingUser}
        templates={templates ?? []}
        onDone={() => setEditingUser(null)}
      />
    );
  }

  return (
    <div className="max-w-4xl">
      {/* Phones: the explanation on top, the action full width under it
          (squeezed beside the text it wrapped the text to a narrow column). */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between [&>button]:justify-center">
        <p className="text-sm text-gray-500">
          Gestionați membrii echipei și accesul acestora. Membrii cu cont de autentificare se pot
          conecta și au o matrice de permisiuni; membrii fără cont sunt doar în director.
        </p>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="ui-btn-primary inline-flex items-center gap-1.5 whitespace-nowrap"
        >
          <UserPlus size={15} />
          Adaugă membru
        </button>
      </div>

      {isLoading && <p className="mt-6 text-sm text-gray-500">Se încarcă echipa...</p>}

      <div className="mt-4 divide-y divide-border rounded-lg border border-border">
        {sorted.length === 0 && !isLoading && (
          <p className="px-4 py-3 text-sm text-gray-400">Niciun membru al echipei încă.</p>
        )}
        {sorted.map((p) => (
          <MemberRow
            key={p.id}
            person={p}
            onEditPermissions={() => setEditingUser(p)}
            onAnonymize={() => setAnonymizeTarget(p)}
          />
        ))}
      </div>

      <CreateTeamMemberModal open={showCreate} onClose={() => setShowCreate(false)} />

      {anonymizeTarget && (
        <AnonymizeDialog person={anonymizeTarget} onClose={() => setAnonymizeTarget(null)} />
      )}
    </div>
  );
}

/**
 * Typed-confirmation dialog for GDPR anonymization. The admin must type the
 * member's email before the destructive action is enabled. Anonymization
 * scrubs PII and disables the login but keeps business rows (tasks, invoices,
 * payroll) intact — it is deliberately not a hard delete.
 */
function AnonymizeDialog({ person, onClose }: { person: Person; onClose: () => void }) {
  const qc = useQueryClient();
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const matches = confirmText.trim().toLowerCase() === person.email.toLowerCase();

  async function handleConfirm() {
    if (!matches || !person.userId) return;
    setError(null);
    setPending(true);
    try {
      await anonymizeUser(person.userId);
      void qc.invalidateQueries({ queryKey: ['people'] });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Anonimizarea a eșuat.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div
      className="ui-modal-backdrop"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ui-modal-panel w-full max-w-[460px] p-5">
        <h3 className="text-base font-semibold text-gray-900">Șterge cont (anonimizare)</h3>
        <p className="mt-1 text-sm text-gray-500">
          Contul <span className="font-medium text-gray-900">{person.fullName}</span> va fi
          dezactivat, iar datele personale (nume, email, telefon) vor fi șterse ireversibil.
          Task-urile, facturile și istoricul de business rămân, atribuite unui „Utilizator șters”.
        </p>
        <label className="mt-4 block text-sm font-medium text-gray-700">
          Tastează emailul membrului pentru confirmare:{' '}
          <span className="font-mono font-normal text-gray-500">{person.email}</span>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="ui-input mt-1 font-mono"
            autoComplete="off"
          />
        </label>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="ui-btn-secondary">
            Anulează
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!matches || pending}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {pending ? 'Se anonimizează…' : 'Anonimizează contul'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MemberRow({
  person,
  onEditPermissions,
  onAnonymize,
}: {
  person: Person;
  onEditPermissions: () => void;
  onAnonymize: () => void;
}) {
  const resend = useResendWelcome();
  const [status, setStatus] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const hasAccount = Boolean(person.userId);

  async function handleGdprExport() {
    if (!person.userId) return;
    setStatus(null);
    setExporting(true);
    try {
      await downloadGdprExport(person.userId);
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Exportul GDPR a eșuat.');
    } finally {
      setExporting(false);
    }
  }

  async function handleResend() {
    setStatus(null);
    try {
      const res = await resend.mutateAsync(person.id);
      if (res.generatedPassword) {
        setStatus(`Parolă nouă: ${res.generatedPassword} (email netrimis)`);
      } else if (res.email?.sent) {
        setStatus('Email de bun venit trimis.');
      } else {
        setStatus('Parolă regenerată.');
      }
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Retrimiterea a eșuat.');
    }
  }

  return (
    <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-900">{person.fullName}</p>
        <p className="truncate text-xs text-gray-400">
          {person.email}
          {person.title ? ` · ${person.title}` : ''}
        </p>
        {status && <p className="mt-0.5 text-xs text-brand-600">{status}</p>}
      </div>
      <div className="flex items-center gap-2">
        {hasAccount ? (
          <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase text-emerald-600">
            <KeyRound size={10} /> Cont
          </span>
        ) : (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-400">
            Doar director
          </span>
        )}
        {hasAccount && (
          <>
            <button
              type="button"
              onClick={onEditPermissions}
              className="inline-flex items-center gap-1 rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Editează permisiuni"
              title="Editează permisiuni"
            >
              <Shield size={14} />
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={resend.isPending}
              className="inline-flex items-center gap-1 rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
              aria-label="Retrimite email de bun venit"
              title="Retrimite email de bun venit"
            >
              <Mail size={14} />
            </button>
            <button
              type="button"
              onClick={handleGdprExport}
              disabled={exporting}
              className="inline-flex items-center gap-1 rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
              aria-label="Exportă date (GDPR)"
              title="Exportă date (GDPR)"
            >
              <Download size={14} />
            </button>
            <button
              type="button"
              onClick={onAnonymize}
              className="inline-flex items-center gap-1 rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
              aria-label="Șterge cont (anonimizare)"
              title="Șterge cont (anonimizare)"
            >
              <UserX size={14} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
